/* eslint-disable prefer-destructuring */
import { config as communitySolarConfig } from '~/funnels/community-solar/config';
import { ALL_URLS } from './urls';

export const DEFAULT_FUNNEL_PATH = communitySolarConfig.path;

declare let Cypress: any | undefined;
export const IS_CYPRESS = typeof Cypress !== 'undefined';
export const IS_TEST = IS_CYPRESS || process.env.ENVIRONMENT === 'test';
export const IS_PRODUCTION = process.env.ENVIRONMENT === 'production';
export const IS_STAGING = process.env.ENVIRONMENT === 'staging';
export const IS_DEV = process.env.ENVIRONMENT === 'development';
export const SENTRY_DSN =
  'https://75a7a56648164b0da4b7cbc50cafec1b@o128365.ingest.sentry.io/5761604';
export const STRIPE_KEY = process.env.STRIPE_KEY!;
export const LD_CLIENT_ID = IS_TEST
  ? '616ec4bf06d2ef0bc1c67739'
  : (process.env.LD_CLIENT_ID as string);

export const AMEREN_TIMEOUT_IN_MS = (() => {
  /**
   * The environment variable always takes priority.
   *
   * If it's not set, then there are two cases.
   *
   * 1) if it's a test(IS_TEST === true), then we use a shorter timeout. You can
   * override this by just setting the environment variable.
   *
   * 2) if it's not a test, then it means we are in staging/production and
   * didn't set or has set the environment variable incorrectly.
   * As a failsafe, it defaults to 30s timeout.
   */
  if (process.env.AMEREN_TIMEOUT_IN_MS) {
    return parseInt(process.env.AMEREN_TIMEOUT_IN_MS);
  } else if (IS_TEST) {
    return 2000;
  }
  return 30000;
})();

const URLS = IS_TEST ? ALL_URLS.staging : ALL_URLS[process.env.ENVIRONMENT!];
export const { API_URL, LOGIN_URL, TERMS_URL, WEBSOCKET_URL, ARCADIA_REWARDS_URL } = URLS;
export const WEBSITE_URL = 'https://arcadia.com';
export const SPONSORED_SOLAR_LANDING_PAGE_URL = 'https://www.arcadia.com/community-solar-national';
export const FORGOT_PASSWORD_URL = `${LOGIN_URL}/forgot-password`;

export const INTERNAL_ROUTES = {
  DELETED_ACCOUNT: '/deleted',
  DOCUSIGN: '/docusign',
  EXPIRED_LOGIN_LINK: '/expired-login-link',
  EXPIRED_SESSION: '/expired',
  RESUMING_REDIRECT: '/resuming-redirect',
  UNEXPECTED: '/unexpected',
  CS_UNAVAILABLE: '/cs-unavailable',
  SPONSORED_SOLAR: '/community-solar-national',
  PAYMENT_VERIFICATION_FAILED: '/payment-verification-failed',
};

export const SPONSORED_SOLAR_ROUTES = {
  UTILITY_SELECTION: 'utility-selection',
  SIGNUP: 'signup',
  USER_CREATION: 'create-user',
  PROOF_DOCS_UPLOAD: 'proof-docs-upload',
  GOOGLE_USER: 'google-user',
  REVIEW_LMI_FORM: 'review-form',
  REVIEW_AND_VIEW_TERMS: 'review-and-view-terms',
  WELCOME_BACK: 'welcome-back',
  UTILITY_CONNECT: 'utility-connect',
  DASHBOARD_ROUTE: 'dashboard/planId/solar?sponsoredSolar=true',
};

export const DASHBOARD_REASONS = {
  BILL_PAY_AVAILABLE: 'billPayAvailable',
  MOVING: 'moving',
  SIGN_DOCUSIGN: 'signDocusign',
  UTILITY_CONNECT_AVAILABLE: 'utilityConnectAvailable',
  VNM_AVAILABLE: 'vnmAvailable',
} as const;

export const AMEREN_URL = IS_TEST
  ? 'https://login.ameren.com/?client_id=myclientid'
  : URLS.AMEREN_URL;

export const LMI_POWERFORM_URL = IS_TEST
  ? 'https://docusign.com/powerform_just_for_arcadia'
  : URLS.LMI_POWERFORM_URL;

export const UTILITY_CONNECT_ENV = (env => {
  if (IS_DEV) return 'local';
  return env;
})(process.env.ENVIRONMENT);

export const DOCU_SIGN_POLLING_INTERVAL = IS_TEST ? 1000 : 5000;
export const DOCU_SIGN_POLLING_TIMEOUT = IS_TEST ? 5000 : 180000;
export const DOCU_SIGN_LMI_STATES = ['NY', 'NJ', 'MD'];

export const CALENDLY_URI_API = IS_TEST
  ? 'https://stoplight.io/mocks/calendly/api-docs/395/users/me'
  : 'https://api.calendly.com/users/me';

export const CALNEDLY_FETCH_DATA_API_BUILDER = (
  userUri: string,
  orgUri: string,
  encodedEmail: string,
) => {
  if (IS_TEST) {
    return `https://stoplight.io/mocks/calendly/api-docs/395/scheduled_events?user=${userUri}&organization=${orgUri}&invitee_email=${encodedEmail}&status=active&count=1`;
  } else {
    return `https://api.calendly.com/scheduled_events?user=${userUri}&organization=${orgUri}&invitee_email=${encodedEmail}&status=active&count=1`;
  }
};

export const CALENDLY_MEET_LINK = 'https://calendly.com/d/cqs6-63j-xwd/business-welcome-call';
export const ZEROBOUNCE_API = 'https://api.zerobounce.net/v2/validate';
export const CEP_MEET_LINK =
  'https://app.acuityscheduling.com/schedule.php?owner=11927610&appointmentType=75332798&ref=embedded_csp';

export const UTILITY_IDS = {
  'Alameda Municipal Power': 2581,
  'Ameren IL': 2441,
  'Ameren MO': 2311,
  'AEP OH': 2342,
  'Atlantic City Electric': 2330,
  'Baltimore Gas & Electric': 2289,
  'Central Hudson Gas & Electric': 2336,
  'Central Maine Power': 2293,
  'Commonwealth Edison': 2269,
  'Consolidated Edison NY': 2337,
  'Delmarva Power DE': 2235,
  'Delmarva Power MD': 2547,
  'Dominion Energy VA': 2387,
  'El Paso Electric NM': 2906,
  'Eversource / NSTAR': 2286,
  'Eversource / WMECO': 2285,
  'Eversource / PSNH': 2326,
  'Jersey Central Power & Light': 2472,
  NYSEG: 2459,
  'National Grid MA': 2284,
  'National Grid NY': 2340,
  'Orange and Rockland': 2902,
  'PEPCO DC': 2233,
  'PEPCO MD': 2287,
  'PSE&G NJ': 2331,
  'PSE&G NY': 2476,
  'Ohio Edison': 2349,
  'Pacific Power OR': 2477,
  'Portland General Electric': 2352,
  'Potomac Edison MD': 2461,
  'Public Service Company of NM': 2334,
  'Rhode Island Energy': 2359,
  'Rochester Gas & Electric': 2339,
  'SDG&E': 2206,
  'Unitil MA': 3005,
  'Versant - Bangor Hydro District': 2292,
  'Versant - Maine Public District': 2295,
  'Xcel Energy CO': 2220,
  'Xcel Energy MN': 2447,
  'Xcel Energy NM': 2901,
} as const;

export const DUAL_UTILITIES: Number[] = [
  UTILITY_IDS['Ameren IL'],
  UTILITY_IDS['Baltimore Gas & Electric'],
  UTILITY_IDS['Central Hudson Gas & Electric'],
  UTILITY_IDS['Central Maine Power'],
  UTILITY_IDS['Commonwealth Edison'],
  UTILITY_IDS['Eversource / WMECO'],
  UTILITY_IDS['Eversource / PSNH'],
  UTILITY_IDS['National Grid MA'],
  UTILITY_IDS['National Grid NY'],
  UTILITY_IDS['PEPCO MD'],
  UTILITY_IDS['Rhode Island Energy'],
  UTILITY_IDS['Versant - Bangor Hydro District'],
  UTILITY_IDS['Versant - Maine Public District'],
  UTILITY_IDS['Eversource / NSTAR'],
  UTILITY_IDS['Unitil MA'],
];

export const STATE_IDS = {
  AL: 1,
  AK: 2,
  AZ: 3,
  AR: 4,
  CA: 5,
  CO: 6,
  CT: 7,
  DE: 8,
  DC: 9,
  FL: 10,
  GA: 11,
  HI: 12,
  ID: 13,
  IL: 14,
  IN: 15,
  IA: 16,
  KS: 17,
  KY: 18,
  LA: 19,
  ME: 20,
  MD: 21,
  MA: 22,
  MI: 23,
  MN: 24,
  MS: 25,
  MO: 26,
  MT: 27,
  NE: 28,
  NV: 29,
  NH: 30,
  NJ: 31,
  NM: 32,
  NY: 33,
  NC: 34,
  ND: 35,
  OH: 36,
  OK: 37,
  OR: 38,
  PA: 39,
  RI: 40,
  SC: 41,
  SD: 42,
  TN: 43,
  TX: 44,
  UT: 45,
  VT: 46,
  VA: 47,
  WA: 48,
  WV: 49,
  WI: 50,
  WY: 51,
} as const;

export const PROMO_CODES = {
  ENERGY_WELL_SIGNUP: 'energywell-cart-signups',
  POWER_SETTER_DATA_ENTRY: 'powersetter-dataentry',
};

export const BlockedStates = (funnel: string) => {
  switch (funnel) {
    case 'business':
      return ['CT'];
    case 'communitySolar':
      return ['CT'];
    case 'telesales':
      return ['CT'];
    case 'telesalesNoPayment':
      return ['CT'];
    case 'constellation':
      return ['CT'];
    case 'nonABP':
      return ['CT'];
    default:
      return ['IL', 'CT'];
  }
};
export const shouldUpdateMetaData = (eventName: string) => {
  return [
    'user_step_completed',
    'utility_account_selection_chosen',
    'utility_account_creation_step_completed',
    'enrollment_step_started',
    'enrollment_step_completed',
    'utility_connect_timed_out',
    'secondary_identifier_added',
    'alternate_enrollment_step_started',
    'upload_lmi_proof_docs_step_started',
    'upload_lmi_proof_docs_step_completed',
    'lmi_document_skipped',
    'lmi_document_upload',
    'billing_step_started',
    'billing_step_completed',
    'welcome_call_scheduled',
    'track_billing_decider_step_started',
    'track_no_billing_step_started',
    'payment_method_created',
    'review_step_started',
    'review_step_completed',
    'legal_step_started',
    'non_lmi_user',
    'legal_step_completed',
    'docusign_lmi_user_signed',
    'funnel_completed',
  ].includes(eventName);
};

export const BLOCKED_STATES_FOR_ENERGYWELL = ['VA', 'IL'];
export const SPONSORED_SOLAR_STATES = ['OH'];
export const SPONSORED_SOLAR_STATES_NAME = ['Ohio'];

export const EMAIL_REGEX = /^cs-subscriber-intake(\+\d+)?@arcadia\.com$/;
